/**
 * Textes des bulles d'aide « ? » du back-office — source unique.
 *
 * Règles de rédaction :
 *   - français, deux phrases au plus ;
 *   - la première dit ce que fait l'écran ou le champ, la seconde la décision
 *     attendue de l'admin ;
 *   - écrit pour un admin qui n'a jamais vu Tribe : créer une quête, la
 *     suivre, valider des collectes et traiter un retrait sans demander d'aide ;
 *   - vocabulaire : Spotter, lieu, quête, collecte, Tip, réputation, concours ;
 *     jamais « POI », jamais « gamifié ».
 *
 * Les composants ne contiennent plus aucun texte d'aide : ils lisent ce fichier.
 */

/* ── Pages d'entrée du menu (une par domaine) ─────────────────────────────── */
export const AIDE_DOMAINE = {
  accueil:
    "Vue d'ensemble de ce qui attend une décision, de l'activité sur le terrain et des chiffres de la semaine. " +
    "Commencez par le bloc « À faire maintenant » et traitez la ligne du haut.",
  traiter:
    "Toutes les décisions en attente, de la plus urgente à la moins urgente : lieux, collectes, quêtes à relire, retraits. " +
    "Ouvrez une ligne, décidez, puis revenez ici jusqu'à ce que la liste soit vide.",
  quetes:
    "Une quête est une mission confiée aux Spotters : une zone, des champs à remplir et une récompense en Tips par collecte validée. " +
    "Créez la quête, publiez-la depuis sa fiche, puis suivez et validez ses collectes au même endroit.",
  lieux:
    "La base des lieux envoyés par les Spotters ou importés du référentiel, avec leur statut. " +
    "Validez les nouveaux lieux, consultez le fichier et tenez à jour les types de lieu.",
  reseau:
    "L'animation des Spotters : activité, réputation, concours et messages. " +
    "Repérez qui progresse et qui décroche, puis décidez qui récompenser, relancer ou bloquer.",
  argent:
    "Les Tips gagnés par les Spotters, leurs retraits en F CFA et les ventes de quêtes aux clients. " +
    "Décidez les retraits en attente et vérifiez que chaque versement porte sa référence Wave.",
  analyses:
    "La couverture du terrain, l'activité et les exports de données. " +
    "Choisissez une zone ou une période, puis exportez ce dont le client ou l'équipe a besoin.",
  reglages:
    "Les paramètres de la plateforme : moteur de prix, produits, types de lieu, contrôles et équipe. " +
    "Chaque modification est tracée : ne changez un réglage que si vous savez ce qui en dépend.",
} as const;

/* ── Accueil et « À traiter » ─────────────────────────────────────────────── */
export const AIDE_ACCUEIL = {
  aFaire:
    "Une ligne par type de décision en attente, la plus urgente en haut ; les trois barres disent depuis quand attend l'élément le plus ancien (1 barre : moins de 2 jours, 2 barres : 2 à 7 jours, 3 barres : au-delà). " +
    "Cliquez sur la ligne du haut et traitez-la en premier.",
  surLeTerrain:
    "La carte montre la zone de chaque quête en cours et, en ocre, les lieux qui attendent une validation. " +
    "Cliquez sur une zone pour ouvrir la quête, ou sur un lieu pour ouvrir sa fiche et le décider.",
  quetesEnCours:
    "Les quêtes publiées, visibles des Spotters, avec leur avancement : collectes reçues rapportées à l'objectif. " +
    "Ouvrez une quête qui stagne pour élargir sa zone, relever sa récompense ou relancer les Spotters.",
  cetteSemaine:
    "Sur 7 jours : les lieux envoyés par les Spotters (hors imports du référentiel), les collectes reçues sur les quêtes et les Spotters actifs. " +
    "Une baisse d'une semaine à l'autre doit vous amener à relancer le réseau.",
} as const;

/* ── Quêtes : liste, relecture, création, fiche ───────────────────────────── */
export const AIDE_QUETES = {
  liste:
    "Toutes les quêtes avec leur statut (brouillon, à valider, publiée, archivée) et l'état du paiement client. " +
    "Ouvrez une quête pour la publier, suivre son avancement ou valider ses collectes.",
  demandesClients:
    "Les quêtes demandées par les clients depuis leur portail, à reprendre avant publication. " +
    "Ouvrez chaque demande, complétez la zone, le prix et les dates, puis validez-la.",
  aValider:
    "Les quêtes et demandes clients qui attendent une relecture avant d'être visibles des Spotters. " +
    "Ouvrez chacune, vérifiez la zone, les champs, la récompense et le budget client, puis publiez-la ou corrigez-la.",
} as const;

export const AIDE_CREER_QUETE = {
  page:
    "Ce formulaire définit une quête de bout en bout : contenu, champs, zone, durée, rémunération, validation et Spotters visés. " +
    "Remplissez les sections dans l'ordre, confirmez le prix et enregistrez : la quête ne sera visible des Spotters qu'après sa publication depuis sa fiche.",
  informations:
    "Le titre et la description s'affichent dans l'application des Spotters ; le client, son email et son logo servent au suivi et aux documents. " +
    "Choisissez un titre court qui dit quoi collecter et où.",
  typesCollecte:
    "Ce que l'on attend du Spotter : recenser des lieux, relever des prix, prendre des photos, répondre à une enquête… " +
    "Cochez au moins le type qui décrit le mieux la mission.",
  typesLieu:
    "Limite la quête à certains types de lieu : le Spotter ne peut envoyer une collecte que sur ces types. " +
    "Laissez vide pour accepter tous les lieux.",
  produits:
    "Pour un relevé de prix, les produits génériques dont le Spotter doit noter le prix, jamais des marques. " +
    "Choisissez-les dans le référentiel, ou laissez vide si la quête ne porte pas sur des produits.",
  challenge:
    "Le mode Challenge fait de la quête un concours : un seul Spotter gagne la récompense en Tips, selon la règle choisie. " +
    "Activez-le seulement si vous désignerez vous-même le gagnant depuis la fiche de la quête.",
  regleVictoire:
    "Comment le gagnant est choisi : premier lieu valide envoyé, meilleur dossier (photos et données) ou Xème Spotter à participer. " +
    "Préférez une règle facile à vérifier, car c'est vous qui désignez le gagnant à la fin.",
  pushViral:
    "Envoie une notification aux seuls Spotters éligibles à la quête au moment de sa publication, avec votre message s'il est rempli. " +
    "Gardez-la pour les challenges qui doivent mobiliser vite.",
  zone:
    "La zone où les collectes sont attendues : une collecte envoyée hors de cette zone n'est jamais validée automatiquement. " +
    "Tracez un rectangle ou un polygone qui couvre exactement le terrain demandé par le client.",
  champs:
    "Le formulaire que le Spotter remplit sur place, dans l'ordre affiché. " +
    "N'ajoutez que ce que le client exploitera, et rendez obligatoires les champs sans lesquels la collecte ne vaut rien.",
  frequence:
    "Le rythme auquel la collecte doit être refaite pendant la quête : une seule fois, ou chaque jour, semaine ou mois. " +
    "Gardez « Une fois » sauf si le client veut suivre une évolution, comme des prix.",
  missionRecurrente:
    "Une mission récurrente se répète par cycles jusqu'à la fin du contrat, et l'estimation affichée multiplie le budget par le nombre de cycles. " +
    "Activez-la pour un client sous contrat suivi ; laissez-la coupée pour une mission ponctuelle.",
  dates:
    "La quête est ouverte aux Spotters entre la date de début et la date de fin. " +
    "Laissez assez de temps au terrain : une quête trop courte atteint rarement son objectif.",
  collectesAttendues:
    "L'objectif affiché aux Spotters comme « cible », qui sert aussi à mesurer l'avancement ; les collectes rejetées ne comptent pas. " +
    "Fixez le nombre attendu par le client, en vous aidant des lieux déjà connus dans la zone.",
  budget:
    "Le prix facturé au client pour la quête, en F CFA. " +
    "Saisissez le montant convenu avec le client : le moteur de prix suggère, mais c'est vous qui décidez.",
  tipsParCollecte:
    "La récompense créditée au Spotter pour chaque collecte validée, en Tips. " +
    "Choisissez un montant qui motive sans dépasser le budget Spotters affiché en dessous.",
  confirmationPrix:
    "L'enregistrement reste bloqué tant que les Tips par collecte et le budget client ne sont pas confirmés. " +
    "Cochez seulement après avoir relu les deux montants : toute modification annule la confirmation.",
  marge:
    "La part du budget client que garde Tribe ; le reste paie les Tips des Spotters. " +
    "Baissez-la si le budget Spotters ne suffit pas à payer l'objectif de collectes.",
  bonusVitesse:
    "Multiplie la récompense des premiers Spotters arrivés, par exemple × 2 pour les 10 premiers. " +
    "Utilisez-le pour lancer vite une quête, en vérifiant que le budget couvre le bonus.",
  validation:
    "En automatique, les collectes qui passent les contrôles (position dans la zone, champs obligatoires remplis) sont validées et leurs Tips crédités sans vous ; les autres attendent votre décision. " +
    "Choisissez le manuel quand la qualité des photos ou des réponses compte pour le client.",
  analysePhoto:
    "Une analyse automatique des photos signale celles qui semblent douteuses ou hors sujet ; elle aide à décider mais ne valide ni ne paie rien. " +
    "Activez-la quand la quête repose sur des photos.",
  cohorte:
    "Réserve la quête aux Spotters qui ont le niveau, l'ancienneté, le statut et le nombre de collectes demandés, et qui se trouvent près de la zone. " +
    "Laissez les critères au minimum pour ouvrir la quête à tous ; resserrez-les pour une mission délicate.",
  notification:
    "Comment prévenir les Spotters à la première publication : notification sur le téléphone, fenêtre à l'ouverture de l'application, ou les deux. " +
    "Écrivez un message court qui dit quoi faire, où, et combien de Tips sont en jeu.",
} as const;

export const AIDE_FICHE_QUETE = {
  page:
    "La fiche d'une quête : son contenu, sa zone, son avancement, sa facturation et les collectes reçues. " +
    "Publiez-la quand elle est prête, puis revenez ici pour valider les collectes et suivre l'objectif.",
  contenu:
    "Ce que les Spotters voient dans l'application : type de collecte, champs à remplir, dates et objectif. " +
    "Pour corriger, utilisez « Modifier » ; une quête publiée doit d'abord être dépubliée.",
  zone:
    "La zone où les collectes sont attendues, avec les lieux déjà connus à l'intérieur. " +
    "Sans zone tracée, le contrôle de position est inactif : ouvrez l'édition pour la tracer avant de publier.",
  tipsReputation:
    "Ce que gagne un Spotter par collecte validée, avec le bonus des premiers arrivés et le tarif des autres Spotters. " +
    "Vérifiez que ces montants sont ceux annoncés au client avant de publier.",
  progression:
    "Les collectes reçues (hors rejetées) rapportées à l'objectif de la quête. " +
    "Si la barre stagne avant la date de fin, élargissez la zone, augmentez la récompense ou relancez les Spotters.",
  facturation:
    "Le client facturé de la quête, ses devis, contrats, factures et avoirs, et ce qui reste à facturer et à encaisser ; chaque document est produit et archivé par le serveur. " +
    "Rattachez d'abord un client complet, émettez le devis, puis facturez en totalité ou par acomptes.",
  collectes:
    "Les collectes envoyées par les Spotters pour cette quête, avec leur photo, leurs contrôles et leur statut. " +
    "Validez une collecte conforme, ce qui crédite définitivement ses Tips au Spotter, ou rejetez-la en choisissant un motif.",
  gagnant:
    "En mode Challenge, vous choisissez le gagnant parmi les collectes validées, selon la règle annoncée. " +
    "La désignation est définitive et crédite la récompense sur son solde.",
} as const;

/* ── Lieux ────────────────────────────────────────────────────────────────── */
export const AIDE_LIEUX = {
  aValider:
    "La file des lieux envoyés par les Spotters, un par un, avec la photo, l'analyse automatique et les contrôles d'intégrité. " +
    "Validez si le lieu existe et que la photo le montre, ce qui crédite ses Tips au Spotter ; sinon refusez avec un motif.",
  fichier:
    "Tous les lieux connus, classés par statut : à valider, approuvés, rejetés, expirés, masqués. " +
    "Ouvrez une fiche pour vérifier un lieu, le masquer s'il ne doit plus apparaître, ou le réafficher.",
  analysePhoto:
    "Le verdict d'une analyse automatique de la photo : conforme, douteux ou non conforme. " +
    "C'est une aide : regardez la photo vous-même avant de décider.",
  integrite:
    "Les contrôles de la capture : position réelle du téléphone, date et lieu de la photo, autres Spotters au même endroit. " +
    "Un signal isolé ne suffit pas à refuser, mais plusieurs signaux sur un même Spotter doivent vous alerter.",
} as const;

/* ── Réseau ───────────────────────────────────────────────────────────────── */
export const AIDE_RESEAU = {
  spotters:
    "Chaque Spotter avec son activité, ses lieux validés, son niveau de réputation et sa dernière collecte. " +
    "Repérez les Spotters inactifs à relancer et les plus actifs à récompenser.",
  acces:
    "Les Spotters classés par risque (taux de rejet, signaux d'intégrité) avec l'état de leur compte : bloquer un compte le suspend, le débloquer le rend actif. " +
    "Bloquez seulement quand l'abus est établi, un compte ou plusieurs à la fois, et débloquez dès que le doute est levé.",
  reputation:
    "Les niveaux de réputation des Spotters, les critères pour les atteindre et les Tips offerts à chaque palier. " +
    "Fixez des paliers atteignables : un Spotter doit toujours voir le prochain niveau à sa portée.",
  concours:
    "Un concours récompense, sur une période, le Spotter qui fait valider le plus de lieux du type ciblé. " +
    "Avant de lancer, vérifiez que des lieux de ce type existent, sinon personne ne gagnera.",
  communications:
    "Écrire aux Spotters par notification et revoir l'historique des envois. " +
    "N'envoyez un message que s'il demande une action précise : trop de messages lassent le réseau.",
  clientsInvites:
    "Un client ne peut créer son compte sur le portail qu'avec une invitation : un lien envoyé à son adresse, valable 7 jours, utilisable une fois. " +
    "Invitez la personne qui signera les devis, avec l'organisation telle qu'elle figure sur les documents ; elle ne pourra pas la changer à l'inscription.",
  etatsInvitation:
    "En attente (lien pas encore utilisé), compte créé, expirée au bout de 7 jours ou annulée (le lien ne fonctionne plus). " +
    "Seule une invitation en attente s'annule ; pour une invitation expirée, invitez de nouveau la même adresse.",
} as const;

/* ── Argent ───────────────────────────────────────────────────────────────── */
export const AIDE_ARGENT = {
  retraits:
    "Les demandes de retrait des Spotters : approuvez ou rejetez avec un motif, puis versez sur leur compte Wave ; au-delà du seuil réglé, un second admin doit confirmer. " +
    "Une fois l'argent parti, marquez le retrait versé avec la référence de la transaction Wave.",
  soldes:
    "Le solde de chaque Spotter en Tips et sa contre-valeur en F CFA. " +
    "Consultez-le pour répondre à un Spotter ou vérifier une demande avant de la décider.",
  finance:
    "Les documents émis par entité et par période, et pour chaque quête le contrat, le facturé, le reste à facturer, l'encaissé et le reste dû. " +
    "Relancez les clients dont le reste dû a passé l'échéance, et facturez ce qui reste à facturer.",
  valeurTip:
    "La valeur d'un Tip en F CFA, le minimum et le plafond mensuel de retrait, et le seuil du second avis. " +
    "Réservé au super admin, avec un motif à chaque changement : ne modifiez que sur décision de Tribe.",
  facturation:
    "Les entités qui émettent devis, contrats et factures, par pays, avec leurs identifiants légaux, conditions de paiement et coordonnées bancaires. " +
    "Le super admin complète une entité avant son premier document ; tant qu'un champ manque, elle n'émet rien.",
  entitePrefixe:
    "Le préfixe des numéros de documents de l'entité (TSN, TCI) : il ne change plus une fois posé. " +
    "Vérifiez-le avant la première émission.",
  entiteCertification:
    "Coché, les factures et avoirs de l'entité attendent une certification fiscale (FNE en Côte d'Ivoire) avant d'être remis au client. " +
    "Laissez coché pour la Côte d'Ivoire.",
} as const;

/* ── Facturation : clients, documents, encaissements, exports ─────────────── */
export const AIDE_FACTURATION = {
  clients:
    "Les clients facturés : l'identité que portent leurs devis, contrats et factures. " +
    "Créez le client avant d'émettre ; tant qu'un champ obligatoire manque, aucun document ne part.",
  identite:
    "La raison sociale et l'adresse exactes du client, telles qu'elles doivent figurer sur ses factures. " +
    "Choisissez le pays du client : il décide des identifiants fiscaux demandés.",
  identifiantsFiscaux:
    "Sénégal : NINEA et RCCM ; Côte d'Ivoire : NCC et RCCM ; autre pays : l'identifiant fiscal de ce pays. " +
    "Recopiez-les depuis un document officiel du client, sans les deviner.",
  numeroTva:
    "Le numéro de TVA du client, exigé sur toute facture, même pour un client exonéré. " +
    "Demandez-le au client s'il ne figure pas sur ses documents.",
  exoneration:
    "Un motif rempli rend le client exonéré : ses documents portent une TVA à 0 % et ce motif imprimé. " +
    "Ne le remplissez que sur justificatif (convention, attestation d'exonération).",
  contact:
    "La personne qui reçoit les documents et le courriel « paiement reçu ». " +
    "Indiquez un email que le client lit vraiment.",
  comptePortail:
    "L'email de connexion au portail client qui pourra lire et télécharger les documents de ce client. " +
    "Laissez vide pour que l'email du contact en tienne lieu.",
  rattachement:
    "Le client facturé de cette quête : ses documents portent son identité au jour de leur émission. " +
    "Choisissez-le avant le premier document ; ensuite, il ne change plus.",
  etatMission:
    "Le contrat (dernier devis ou contrat émis), ce qui en est déjà facturé, ce qui reste à facturer, l'encaissé et le reste dû, toutes factures confondues. " +
    "Un reste à facturer attend une facture d'acompte ou de solde ; un reste dû attend un encaissement.",
  devis:
    "Un devis ou un contrat se compose de lignes : libellé, quantité, prix unitaire HT et taux de TVA ; le serveur calcule les montants. " +
    "Vérifiez les totaux avant d'émettre : une fois émis, le document ne change plus et gèle le budget de la quête.",
  facture:
    "Une facture reprend le dernier devis ou contrat : en totalité, en acompte (pourcentage ou montant TTC), ou en solde quand une partie est déjà facturée. " +
    "Choisissez le mode selon ce qui a été convenu avec le client ; le serveur refuse tout dépassement du contrat.",
  resteAFacturer:
    "Ce qui reste à facturer sur le contrat, factures non annulées déduites. " +
    "Un acompte ne peut pas le dépasser ; la facture de solde le reprend exactement.",
  avoir:
    "Un avoir corrige une facture émise, qui elle ne change jamais : total pour l'annuler, partiel pour une remise ou une erreur sur une ligne. " +
    "Indiquez toujours le motif, il figure sur l'avoir.",
  documents:
    "Les documents émis pour cette quête, du plus ancien au plus récent, avec leur PDF archivé par le serveur. " +
    "Ouvrez une facture pour voir son reste dû, ses avoirs et ses encaissements.",
  suiviFacture:
    "Le reste dû d'une facture : son TTC, moins ses avoirs, moins ses encaissements, et l'état qui en découle. " +
    "Saisissez un encaissement dès que l'argent est reçu, avec sa référence.",
  encaissement:
    "Une somme reçue pour cette facture : montant en XOF, date de valeur, mode et référence de la transaction. " +
    "Ne saisissez que ce qui est effectivement arrivé sur le compte ; une erreur se corrige par une annulation, jamais par une modification.",
  tropPercu:
    "Le serveur a refusé ce montant : il dépasse le reste dû de la facture. " +
    "Cochez seulement si le client a réellement trop payé ; le trop-perçu devra lui être remboursé ou déduit.",
  annulation:
    "Annule cet encaissement par une contre-écriture du même montant en négatif ; l'original reste visible. " +
    "Le motif est obligatoire et sert au contrôle comptable.",
  ventesFiltres:
    "L'entité émettrice et la période (date d'émission, heure de Dakar) des documents affichés et exportés. " +
    "Choisissez un mois complet pour les échanges avec l'expert-comptable.",
  exports:
    "Le journal des ventes, celui des encaissements et le détail des lignes, en CSV tels que le serveur les produit. " +
    "Transmettez-les sans les retoucher : ils sont la référence comptable de la période.",
  etatParQuete:
    "Pour chaque quête facturée de la période : contrat, facturé, reste à facturer, encaissé et reste dû. " +
    "Commencez par les lignes au reste dû le plus élevé.",
} as const;

/* ── Analyses ─────────────────────────────────────────────────────────────── */
export const AIDE_ANALYSES = {
  carte:
    "Les lieux de la zone visible, colorés par statut, avec leur répartition par catégorie. " +
    "Déplacez la carte sur un quartier pour voir ce qui est couvert et ce qui manque.",
  exports:
    "Les lieux approuvés, filtrés par zone, type et période, à exporter en fichier ou en rapport. " +
    "Choisissez l'export client pour un livrable et l'export interne pour l'équipe.",
  comparaison:
    "Compare les lieux de Tribe à ceux de Google et du référentiel, par catégorie et sur la carte. " +
    "Repérez les catégories où Tribe est en retard pour cibler les prochaines quêtes.",
} as const;

/* ── Réglages ─────────────────────────────────────────────────────────────── */
export const AIDE_REGLAGES = {
  moteurPrix:
    "Les paramètres du moteur qui suggère un prix pour chaque quête : marges, planchers, plafonds et récompenses de référence. " +
    "Le moteur ne fait que suggérer, l'admin fixe toujours le prix : ne changez ces valeurs que sur décision de Tribe.",
  produits:
    "Le référentiel des produits génériques proposés dans les relevés de prix, jamais des marques. " +
    "Ajoutez un produit manquant, ou désactivez celui qui ne doit plus apparaître dans les nouvelles quêtes.",
  typesLieu:
    "Les catégories et sous-types de lieu que voient les Spotters et les clients. " +
    "Un sous-type ne se supprime pas : désactivez-le, les lieux déjà saisis gardent leur valeur.",
  parametres:
    "Les contrôles de la plateforme : fraîcheur des lieux, champs supplémentaires, analyse des photos, intégrité des captures et comptes testeurs. " +
    "Chaque modification est tracée ; ne changez un réglage que si vous en connaissez l'effet sur les Spotters.",
  equipe:
    "Les comptes de l'équipe Tribe, des clients et des Spotters, avec leurs rôles. " +
    "Donnez à chaque admin le rôle minimal dont il a besoin, et retirez l'accès dès qu'il quitte l'équipe.",
} as const;
